import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Store } from "@ngrx/store";

import * as M from "../data/model";
import { productDefinitions } from "../data/product.constants";
import {
    isSectionDefinition,
    isProductDefinition,
    SectionDefinition,
    ProductDefinition,
    UserInfo,
} from "../data/product";
import { RootState } from "../store/root";
import { Update, SetOffer } from "../store/product-index/actions";
import { IndexSection, IndexInstance } from "../store/product-index/state";
import { stateBuilder } from "../store/utils";
import { assert } from "../utils/debug";
import { KeycloakService } from "../security";

// TODO: This should not be a service, but a reducer
@Injectable()
export class ProductIndexService {
    state$: Observable<LocalState>;

    constructor(
        private store: Store<RootState>,
        private keycloak: KeycloakService,
    ) {
        this.state$ = LocalState.create(store);
        this.state$.subscribe((state) => {
            const index = this.renderProductIndex(state.offers, null);
            this.store.dispatch(new Update(index));

            // TODO: Also triggers on irrelevant edits
            // TODO: What to do when customOffer == null?
            const customOffer = this.findCustomOffer(state.offers);
            if (
                customOffer &&
                (state.customOffer == null ||
                    state.customOffer.Id !== customOffer.Id)
            ) {
                // This might loop
                this.store.dispatch(new SetOffer(customOffer));
            }
        });
    }

    renderProductIndex(
        offers: M.Offer[],
        lastIndex: IndexSection,
    ): IndexSection {
        const divisions: M.Division[] = [].concat(
            [],
            ...offers.map((o) => o.Divisions),
        );
        const userInfo = new UserInfo(this.keycloak.getExistingParsedToken());
        const newIndex = this.makeIndex(
            productDefinitions,
            divisions,
            userInfo,
        ) as IndexSection;
        const index = this.diffIndex(lastIndex, newIndex);
        return index as IndexSection;
    }

    makeIndex(
        def: SectionDefinition | ProductDefinition,
        divisions: M.Division[],
        userInfo: UserInfo,
    ): IndexSection {
        const section: IndexSection = {
            name: def.name,
            application: null,
            category: null,
            line: null,
            enabled: true,
            canAdd: false,
            items: [],
            instances: [],
            visibility: null,
        };
        if (isSectionDefinition(def)) {
            section.items = def.sections
                .filter(
                    (subdef) =>
                        !subdef.visibility || subdef.visibility(userInfo),
                )
                .map((subdef) => this.makeIndex(subdef, divisions, userInfo));
        } else if (isProductDefinition(def)) {
            section.application = def.application;
            section.category = def.category;
            section.line = def.line;
            section.canAdd = true;
            section.visibility = def.visibility
                ? (cp) => def.visibility(userInfo, cp)
                : section.visibility;
            const instances = [];
            divisions.forEach((div, i) => {
                if (div.Name === def.name.displayName) {
                    instances.push(div);
                }
            });
            divisions = divisions.filter((div) => !instances.includes(div));
            section.instances = instances.map(
                (div: M.Division) =>
                    ({
                        name: {
                            key: def.name.key,
                            displayName: div.Label,
                            printName: def.name.printName,
                        },
                        line: def.line,
                        enabled: true,
                        canDelete: !div.ExternalId && !!div.Id && !div.$remove,
                        isWaiting: !div.Id || div.$remove,
                        productVersion: div.ProductVersion,
                        division: div,
                    }) as IndexInstance,
            );
        } else {
            assert(false, "Definition is neither section nor product.");
        }
        if (section.items.length > 0 && !def.multi) {
            section.canAdd = false;
        }
        return section;
    }

    diffIndex(
        prev: IndexSection | IndexInstance,
        cur: IndexSection | IndexInstance,
    ) {
        // TODO: Implement
        return cur;
    }

    findCustomOffer(offers: M.Offer[]): M.Offer {
        const candidates = offers.filter(
            (o) => o.ExternalId === "" || o.ExternalId == null,
        );
        if (candidates.length > 0) {
            return candidates[0];
        } else {
            return null;
        }
    }
}

class LocalState {
    offers: M.Offer[];
    customOffer: M.Offer;

    static create(store: Store<RootState>): Observable<LocalState> {
        return stateBuilder(store, new LocalState())
            .apply("offers", (s) => s.model.ConsultationProcess.Protocol.Offers)
            .apply("customOffer", (s) => s.productIndex.customOffer).state$;
    }
}
